import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Text,
  Pivot,
  PivotItem,
  DefaultButton,
  Spinner,
} from "@fluentui/react";
import {
  Application,
  ApplicationDefaultType,
  ApplicationCustomerDefaultSeed,
  getApplicationCustomerDefaultSeed,
  initOpportunityDefaultMetrics,
  createApplicationCustomerDefaultSeed,
} from "@/api/applications";
import { Customer } from "@/api/customers";

interface Props {
  application: Application;
  customer: Customer;
  defaultTypes: ApplicationDefaultType[];
  isOpen: boolean;
  onDismiss: () => void;
}

const AppCustomerDefaultValuesModal = (props: Props) => {
  const [hidden, setHidden] = useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [loadingSeed, setLoadingSeed] = useState<boolean>(false);
  const [seedByTypeId, setSeedByTypeId] = useState<
    Record<number, ApplicationCustomerDefaultSeed | null>
  >({});
  const [seeding, setSeeding] = useState<boolean>(false);
  const [savingResult, setSavingResult] = useState<boolean>(false);
  const [seedErrorByTypeId, setSeedErrorByTypeId] = useState<
    Record<number, string | null>
  >({});

  useEffect(() => {
    setHidden(!props.isOpen);
  }, [props.isOpen]);
  useEffect(() => {
    if (props.defaultTypes && props.defaultTypes.length > 0) {
      setSelectedKey(props.defaultTypes[0].Id.toString());
    } else {
      setSelectedKey(undefined);
    }
  }, [props.defaultTypes]);
  useEffect(() => {
    if (selectedKey) {
      const id = Number(selectedKey);
      if (!Number.isNaN(id)) {
        loadSeedIfNeeded(id);
      }
    }
  }, [selectedKey]);

  const onDismiss = () => {
    setHidden(true);
    props.onDismiss();
  };
  const loadSeedIfNeeded = async (defaultTypeId: number) => {
    if (seedByTypeId[defaultTypeId] !== undefined) {
      return;
    }
    setLoadingSeed(true);
    try {
      const seed = await getApplicationCustomerDefaultSeed(
        props.application.Id,
        props.customer.Id,
        defaultTypeId
      );
      setSeedByTypeId((prev) => ({ ...prev, [defaultTypeId]: seed }));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setSeedByTypeId((prev) => ({ ...prev, [defaultTypeId]: null }));
      } else {
        setSeedByTypeId((prev) => ({ ...prev, [defaultTypeId]: null }));
      }
    } finally {
      setLoadingSeed(false);
    }
  };

  const onTabClick = (item?: PivotItem) => {
    const key = item?.props.itemKey;
    setSelectedKey(key);
    if (key) {
      const id = Number(key);
      if (!Number.isNaN(id)) {
        loadSeedIfNeeded(id);
      }
    }
  };

  const isOpportunityDefaultMetricsType = (defaultTypeId: number) => {
    const defaultType = props.defaultTypes.find((dt) => dt.Id === defaultTypeId);
    return (
      props.application?.Name === "Pcs Opportunity Api" &&
      defaultType?.Name === "Default Metrics"
    );
  };

  const getSeedButtonText = (defaultTypeId: number) => {
    const defaultType = props.defaultTypes.find((dt) => dt.Id === defaultTypeId);
    return defaultType?.Name
      ? `Seed "${defaultType.Name}"`
      : "Seed Setting Type default values";
  };

  const getErrorMessageForStatus = (status?: number) => {
    if (status === 401) {
      return "You are not authorized to seed default values. Please sign in again or contact an administrator.";
    }
    if (status === 400) {
      return "The request to seed default values is invalid. Please verify the customer and try again.";
    }
    if (status === 500) {
      return "An unexpected error occurred while seeding default values. Please try again later.";
    }
    return "Failed to seed default values. Please try again.";
  };

  const runSeeding = async (defaultTypeId: number) => {
    let success = true;
    let errorMessage: string | null = null;
    setSeedErrorByTypeId((prev) => ({ ...prev, [defaultTypeId]: null }));
    try {
      setSeeding(true);
      const response = await initOpportunityDefaultMetrics(props.customer.Id);
      success = response?.status === 202;
      if (!success) {
        errorMessage = getErrorMessageForStatus(response?.status);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      errorMessage = getErrorMessageForStatus(status);
      success = false;
    } finally {
      setSeeding(false);
    }
    try {
      setSavingResult(true);
      const saved = await createApplicationCustomerDefaultSeed(
        props.application.Id,
        props.customer.Id,
        defaultTypeId,
        success
      );
      setSeedByTypeId((prev) => ({ ...prev, [defaultTypeId]: saved }));
    } catch (error: any) {
      const status = error?.response?.status;
      if (!errorMessage) {
        errorMessage = getErrorMessageForStatus(status);
      }
    } finally {
      setSavingResult(false);
      if (errorMessage) {
        setSeedErrorByTypeId((prev) => ({ ...prev, [defaultTypeId]: errorMessage }));
      }
    }
  };

  const renderSeedContent = (defaultTypeId: number) => {
    if (loadingSeed && seedByTypeId[defaultTypeId] === undefined) {
      return <Spinner label="Loading default values..." />;
    }
    const seed = seedByTypeId[defaultTypeId];
    if (!seed) {
      const isOpportunityDefaultMetrics = isOpportunityDefaultMetricsType(defaultTypeId);
      const buttonText = getSeedButtonText(defaultTypeId);
      const errorMessage = seedErrorByTypeId[defaultTypeId];
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 8,
          }}
        >
          <Text>No default values have been seeded for this setting type.</Text>
          {(seeding || savingResult) && (
            <Spinner label={seeding ? "Seeding data" : "Saving seeding result"} />
          )}
          <DefaultButton
            text={
              isOpportunityDefaultMetrics
                ? buttonText
                : "Seed Setting Type default values"
            }
            disabled={!isOpportunityDefaultMetrics || seeding || savingResult}
            onClick={async () => {
              if (!isOpportunityDefaultMetrics) {
                return;
              }
              await runSeeding(defaultTypeId);
            }}
          />
          {errorMessage && (
            <Text
              variant="small"
              styles={{ root: { color: "#a80000" } }}
            >
              {errorMessage}
            </Text>
          )}
        </div>
      );
    }
    const createdAt = seed.CreatedDateUtc
      ? new Date(
          seed.CreatedDateUtc.endsWith("Z")
            ? seed.CreatedDateUtc
            : `${seed.CreatedDateUtc}Z`
        ).toLocaleString()
      : "";
    const isOpportunityDefaultMetrics = isOpportunityDefaultMetricsType(defaultTypeId);
    const buttonText = getSeedButtonText(defaultTypeId);
    const errorMessage = seedErrorByTypeId[defaultTypeId];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Text>
          {`Default values configuration for "${
            props.defaultTypes.find((dt) => dt.Id === defaultTypeId)?.Name
          }".`}
        </Text>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text>Success:</Text>
          <span
            className={seed.Success ? "seed-badge seed-badge-success" : "seed-badge seed-badge-failure"}
          >
            {seed.Success ? "Yes" : "No"}
          </span>
        </div>
        <Text>
          Last executed at: <strong>{createdAt}</strong>
        </Text>
        {(seeding || savingResult) && (
          <Spinner label={seeding ? "Seeding data" : "Saving seeding result"} />
        )}
        <DefaultButton
          text={
            isOpportunityDefaultMetrics
              ? buttonText
              : "Seed Setting Type default values"
          }
          disabled={!isOpportunityDefaultMetrics || seeding || savingResult}
          onClick={async () => {
            if (!isOpportunityDefaultMetrics) {
              return;
            }
            await runSeeding(defaultTypeId);
          }}
        />
        {errorMessage && (
          <Text
            variant="small"
            styles={{ root: { color: "#a80000" } }}
          >
            {errorMessage}
          </Text>
        )}
      </div>
    );
  };

  return (
    <Dialog
      className="default-values-dialog"
      hidden={hidden}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Default Values",
      }}
      modalProps={{
        isBlocking: false,
      }}
    >
      <div className="default-values-dialog-content">
        <div>
          <Text>
            Manage default values for customer{" "}
            <strong>{props.customer?.Name}</strong> in application{" "}
            <strong>{props.application?.Name}</strong>.
          </Text>
        </div>
        <div
          style={{
            borderBottom: "1px solid #edebe9",
            marginTop: 12,
            marginBottom: 12,
          }}
        />
        <div>
          {props.defaultTypes && props.defaultTypes.length > 0 ? (
            <Pivot selectedKey={selectedKey} onLinkClick={onTabClick}>
              {props.defaultTypes.map((dt) => (
                <PivotItem
                  headerText={dt.Name}
                  itemKey={dt.Id.toString()}
                  key={dt.Id}
                >
                  <div style={{ marginTop: 8 }}>{renderSeedContent(dt.Id)}</div>
                </PivotItem>
              ))}
            </Pivot>
          ) : (
            <Text>No default types configured for this application.</Text>
          )}
        </div>
        <DialogFooter className="default-values-dialog-footer">
          <PrimaryButton onClick={onDismiss} text="Close" />
        </DialogFooter>
      </div>
    </Dialog>
  );
};

export default AppCustomerDefaultValuesModal;
